import { createFileRoute, redirect } from '@tanstack/react-router'
import { LoginForm } from '@/features/auth/login-form'
import { currentUserQuery } from '@/lib/auth'

/**
 * Route shell. Definition and guards only — the form lives in a feature.
 *
 * The `redirect` search param is written by the `_authenticated` guard when
 * it sends an unauthenticated visitor here; the form honours it after a
 * successful login so the visitor lands back where they were headed.
 *
 * The `beforeLoad` is `_authenticated`'s guard in reverse: a valid session
 * bounces off `/login` to the dashboard. It is load-bearing for the same
 * reason it is in the company app — submitting the sign-in form while a
 * session exists makes the login handler 302 the POST away from the form
 * page, and the fetch follows that redirect with `Accept: application/json`,
 * which the dev server answers with a 404. A second account is tested by
 * signing out first; this guard makes that explicit instead of a 404.
 */
export const Route = createFileRoute('/login')({
  beforeLoad: async ({ context }) => {
    let authenticated = false
    try {
      await context.queryClient.ensureQueryData(currentUserQuery)
      authenticated = true
    } catch {
      // Login is the reverse guard: any session-check failure means the form
      // must remain available, including transient and server-side failures.
      authenticated = false
    }
    if (authenticated) {
      throw redirect({ to: '/' })
    }
  },
  component: LoginForm,
  validateSearch: (search: Record<string, unknown>): { redirect?: string } => ({
    redirect: typeof search.redirect === 'string' ? search.redirect : undefined,
  }),
})
